import { Map, List } from 'immutable';
import { either, some } from '../../util';

// /////////////////////////////////////////////////////////
//
// Form queries
//
// /////////////////////////////////////////////////////////

function getFieldsSteps(state, view, form) {
  return state.getIn(['views', view, 'forms', form, 'fieldsSteps'], List());
}

function showErrors(state, view, form) {
  const show = state.getIn(['views', view, 'forms', form, 'showErrors']);
  return either(show, false);
}

function queryData(state, view, form, queryFn) {
  const data = state.getIn(['views', view, 'forms', form, 'data']) || Map();
  const values = state.getIn(['views', view, 'forms', form, 'values']) || Map();
  const result = queryFn(data, values);

  return result;
}

function queryErrors(state, view, form, queryFn) {
  const formState = state.getIn(['views', view, 'forms', form]) || Map();
  const errors = formState.get('errors') || Map();
  const showErrors = formState.get('showErrors');
  return queryFn(
    errors.filter((v) => Boolean(v)),
    showErrors,
  );
}

function countMultifieldErrors(acc, m) {
  if (acc > 0) {
    return acc;
  }
  if (!m || m.size < 1) {
    return acc;
  }
  return (
    acc +
    m.reduce((reduction, e) => {
      if (reduction > 0) {
        return reduction;
      }
      const tick = e && e.length > 0 ? 1 : 0;
      return reduction + tick;
    }, 0)
  );
}

function createCountErrors(fields) {
  return (acc, value, key) => {
    if (acc > 0 || !value || (fields && !fields.has(key))) {
      return acc;
    }
    if (typeof value === 'string' && value.length > 0) {
      return acc + 1;
    }
    return acc + value.reduce(countMultifieldErrors, 0);
  };
}

function getStepFields(state, view, form, step) {
  if (step < 0) {
    return null;
  }
  const steps = getFieldsSteps(state, view, form);
  const fields = steps.get(step);
  return fields && fields.size > 0 ? fields : null;
}

function hasErrors(state, view, form, step = -1) {
  const fields = getStepFields(state, view, form, step);
  const errorsKeyPath = ['views', view, 'forms', form, 'errors'];
  const errors = state.getIn(errorsKeyPath) || Map();
  return errors.reduce(createCountErrors(fields), 0) > 0;
}

function hasRemoteValidations(state, view, form, step = -1) {
  const fields = getStepFields(state, view, form, step);
  const errorsKeyPath = ['views', view, 'forms', form, 'remoteValidations'];
  const errors = state.getIn(errorsKeyPath) || Map();
  return errors.reduce(createCountErrors(fields), 0) > 0;
}

// /////////////////////////////////////////////////////////
//
// Field queries
//
// /////////////////////////////////////////////////////////

function getSimpleFieldState(formState, field, showError) {
  const data = formState.getIn(['data', field]);
  const value = formState.getIn(['values', field]);

  return {
    value,
    data,
    focus: formState.getIn(['focus', field]),
    error: formState.getIn(['errors', field]),
    validation: formState.getIn(['remoteValidations', field]),
    showError,
  };
}

function getMultiFieldState(formState, multifield, index, field, showError) {
  const data = formState.getIn(['data', multifield, index, field]);
  let value = formState.getIn(['values', multifield, index, field]);
  if (!some(value)) {
    value = '';
  }

  return {
    value,
    data,
    focus: formState.getIn(['focus', multifield, index, field]),
    error: formState.getIn(['errors', multifield, index, field]),
    validation: formState.getIn([
      'remoteValidations',
      multifield,
      index,
      field,
    ]),
    showError,
  };
}

function getFieldState(state, view, form, { field, multifield, index }) {
  const formState = state.getIn(['views', view, 'forms', form], Map());
  const showError = formState.getIn(['showErrors']);

  if (!some(index)) {
    return getSimpleFieldState(formState, field, showError);
  }
  return getMultiFieldState(formState, multifield, index, field, showError);
}

function getMultifieldCount(state, form, view, multifield) {
  const count = state.getIn([
    'views',
    view,
    'forms',
    form,
    'multifieldCount',
    multifield,
  ]);

  const values = state.getIn([
    'views',
    view,
    'forms',
    form,
    'values',
    multifield,
  ]);

  const mCount = some(count) ? count : 1;
  const mValue = some(values) ? values.size : 1;

  if (mCount > mValue) {
    return mCount;
  }
  return mValue;
}

// /////////////////////////////////////////////////////////
//
// Export
//
// /////////////////////////////////////////////////////////

const queries = {
  hasErrors,
  queryData,
  showErrors,
  queryErrors,
  getFieldState,
  getFieldsSteps,
  getMultifieldCount,
  hasRemoteValidations,
};

export default queries;
